import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AuthShell } from "@/components/AuthShell";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const user = await login(email, password);
      navigate(user.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell quote={<>MOVE LIKE<br />A <span className="text-sky-300">BEAST.</span></>} author="— The ShipZilla Control Tower">
      <div data-testid="login-page">
        <h1 className="font-display font-black tracking-tight text-4xl">Login</h1>
        <p className="text-sm text-zinc-500 mt-2">
          Same account as the mobile app — your orders, wallet and KYC sync instantly.
        </p>
        <form onSubmit={submit} className="mt-10 space-y-5" data-testid="login-form">
          {error && (
            <div data-testid="login-error-alert" className="border-2 border-red-600 bg-red-50 text-red-700 text-sm font-semibold px-4 py-3">
              {error}
            </div>
          )}
          <div>
            <label htmlFor="login-email" className="text-xs font-mono font-bold uppercase tracking-[0.2em]">Email</label>
            <input
              id="login-email"
              data-testid="login-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="demo@shipzilla.in"
              className="mt-2 w-full border-2 border-obsidian bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian"
            />
          </div>
          <div>
            <label htmlFor="login-password" className="text-xs font-mono font-bold uppercase tracking-[0.2em]">Password</label>
            <input
              id="login-password"
              data-testid="login-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              className="mt-2 w-full border-2 border-obsidian bg-white px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-offset-2 focus:ring-obsidian"
            />
          </div>
          <button
            type="submit"
            data-testid="login-submit-button"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-2 bg-acid text-white font-bold px-6 py-4 border-2 border-obsidian shadow-[4px_4px_0px_0px_#0A0A0A] hover:shadow-none hover:translate-x-[4px] hover:translate-y-[4px] transition-all duration-150 disabled:opacity-60"
          >
            {loading ? "Entering…" : "Enter ShipZilla"} <ArrowRight className="w-4 h-4" />
          </button>
        </form>
        <p className="text-sm text-zinc-500 mt-8">
          New merchant?{" "}
          <Link to="/signup" data-testid="login-signup-link" className="font-bold text-obsidian underline decoration-acid decoration-4 underline-offset-4">
            Create free account
          </Link>
        </p>
      </div>
    </AuthShell>
  );
}
